/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import SearchAutoCompleteProductAction from "@eisinc/App/Components/SearchAutoCompleteProductAction/SearchAutoCompleteProductAction";
import Button from "@eisinc/MobiusOverrides/Button";
import LazyImage, { LazyImageProps } from "@eisinc/MobiusOverrides/LazyImage";
import { TextFieldPresentationProps } from "@eisinc/MobiusOverrides/TextField";
import ProductAddToCartButton from "@eisinc/Overrides/Components/ProductAddToCartButton";
import ProductQuantityOrdered from "@eisinc/Overrides/Components/ProductQuantityOrdered";
import { screen } from "@eisinc/XDL/Helpers";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel, ProductAutocompleteItemModel } from "@insite/client-framework/Types/ApiModels";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { css } from "styled-components";

interface Props {
    products: ProductAutocompleteItemModel[];
    focusedItem?: AutocompleteItemModel;
    goToUrl: (url: string) => void;
    extendedStyles?: AutocompleteProductsStyles;
}

export interface AutocompleteProductsStyles {
    container?: InjectableCss;
    focusedContainer?: InjectableCss;
    image?: LazyImageProps;
    infoGridItem?: InjectableCss;
    titleLink?: LinkPresentationProps;
    extendedQtyFieldStyles?: TextFieldPresentationProps;
    extendedAddToCartStyles?: InjectableCss;
    productDetailsWrapper?: InjectableCss;
    productNumbersWrapper?: InjectableCss;
    productNumberLabel?: TypographyPresentationProps;
    productNumber?: TypographyPresentationProps;
    productActionsWrapper?: InjectableCss;
}

const productContainerStyle = css`
    display: flex;
    justify-content: flex-end;
    margin: 0;
    padding: 15px;
    cursor: pointer;
    width: 50%;
    flex: 0 1 50%;
    text-align: left;
    border-bottom: 1px solid ${getColor("common.ui3")};

    ${screen("lte", "lg")`
        width: 100%;
        flex: 1 1 100%;
    `}

    ${screen("lte", "md")`
        width: 50%;
        flex: 0 1 50%;
    `}

    ${screen("lte", "sm")`
        width: 100%;
        flex: 1 1 100%;
    `}
`;

export const autocompleteProductsStyles: AutocompleteProductsStyles = {
    container: {
        css: productContainerStyle,
    },
    focusedContainer: {
        css: css`
            ${productContainerStyle}
            background-color: lightgray;
        `,
    },
    image: {
        width: "75px",
        height: "75px",
        css: css`
            flex: 0 0 75px;
            margin-right: 10px;
            img {
                width: auto;
                height: auto;
                max-width: 100%;
                max-height: 100%;
            }
        `,
    },
    productDetailsWrapper: {
        css: css`
            display: flex;
            @media (max-width: 481px) {
                flex-wrap: wrap;
            }
        `,
    },
    productNumbersWrapper: {
        css: css`
            flex: 1 1 auto;
            line-height: 1;

            > .product-num {
                display: flex;
            }
            @media (max-width: 481px) {
                width: 100%;
            }
        `,
    },
    productNumberLabel: {
        size: "12px",
        lineHeight: "13px",
        weight: "bold",
        css: css`
            white-space: nowrap;
        `,
    },
    productNumber: {
        size: "12px",
        lineHeight: "13px",
    },
    productActionsWrapper: {
        css: css`
            display: flex;
            flex: 0 0 auto;
            @media (max-width: 481px) {
                margin-top: 10px;
            }
        `,
    },
    infoGridItem: {
        css: css`
            display: flex;
            flex-direction: column;
            max-width: calc(100% - 85px);
            flex-basis: calc(100% - 85px);

            .choose-from-options {
                height: 32px;
                min-height: 32px;
                margin-top: 10px;
            }
        `,
    },
    titleLink: {
        color: "text.main",
        typographyProps: {
            size: "14px",
            lineHeight: "18px",
            css: css`
                width: 100%;
                margin: 0 0 10px 0;
                text-transform: uppercase;
            `,
        },
    },
    extendedQtyFieldStyles: {
        cssOverrides: {
            formField: css`
                margin-right: 10px;
                height: 32px;
                width: 52px;
                text-align: center;
            `,
            inputSelect: css`
                height: 32px;
                text-align: center;
            `,
        },
    },
    extendedAddToCartStyles: {
        css: css`
            height: 32px;
            min-height: 32px !important;
            line-height: 14px;
            width: 45px;
        `,
    },
};

const styles = autocompleteProductsStyles;

const productActionsWrapperClickHandler = (e: any) => e.stopPropagation();

class AutocompleteProducts extends React.Component<Props> {
    private readonly styles: AutocompleteProductsStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
    }

    render() {
        let { products } = this.props;

        products = products.map(product => {
            return {
                ...product,
                canAddToCart: product.properties.canAddToCart === "true",
                canEnterQuantity: product.properties.canEnterQuantity === "true",
                minimumOrderQty: Number(product.properties.minimumOrderQty),
                qtyOrdered: 1,
            };
        });
        const styles = this.styles;
        return (
            <>
                {products.map(product => (
                    <StyledWrapper
                        {...(this.props.focusedItem === product ? styles.focusedContainer : styles.container)}
                        key={`${product.id!} ${Math.random()}`}
                        onClick={() => {
                            this.props.goToUrl(product.url);
                        }}
                        className="autocomplete-products-container"
                    >
                        <LazyImage {...styles.image} src={product.image} />
                        <StyledWrapper {...styles.infoGridItem} className="autocomplete-products-info-grid-item">
                            <Link {...styles.titleLink} className="autocomplete-products-link">{product.displayTitle}</Link>

                            <StyledWrapper {...styles.productDetailsWrapper}>
                                <StyledWrapper {...styles.productNumbersWrapper}>
                                    {product.properties.yourPartNumber && (
                                        <StyledWrapper className="product-num">
                                            <Typography {...styles.productNumberLabel}>
                                                {siteMessage("Custom_Your_Part_#", "Your Part #:")}&nbsp;
                                            </Typography>
                                            <Typography {...styles.productNumber}>
                                                {product.properties.yourPartNumber}
                                            </Typography>
                                        </StyledWrapper>
                                    )}
                                    {product.properties.specNumber && (
                                        <StyledWrapper className="product-num">
                                            <Typography {...styles.productNumberLabel}>
                                                {siteMessage("Custom_Spec_#", "Spec #:")}&nbsp;
                                            </Typography>
                                            <Typography {...styles.productNumber}>
                                                {product.properties.specNumber}
                                            </Typography>
                                        </StyledWrapper>
                                    )}

                                    {!product.properties.specNumber &&
                                        !product.properties.yourPartNumber &&
                                        product.manufacturerItemNumber && (
                                            <StyledWrapper className="product-num">
                                                <Typography {...styles.productNumberLabel}>
                                                    {siteMessage("Custom_MFR_#", "MFR #:")}&nbsp;
                                                </Typography>
                                                <Typography {...styles.productNumber}>
                                                    {product.manufacturerItemNumber}
                                                </Typography>
                                            </StyledWrapper>
                                        )}
                                    {(!product.properties.specNumber || !product.properties.yourPartNumber) && (
                                        <StyledWrapper className="product-num">
                                            <Typography {...styles.productNumberLabel}>
                                                {siteMessage("Custom_Part_#", "EIS Part #:")}&nbsp;
                                            </Typography>
                                            <Typography {...styles.productNumber}>
                                                {product.displayErpNumber}
                                            </Typography>
                                        </StyledWrapper>
                                    )}
                                </StyledWrapper>
                                {product.properties.isVariantParent !== "true" && (
                                    <SearchAutoCompleteProductAction product={product}>
                                        <StyledWrapper
                                            {...styles.productActionsWrapper}
                                            onClick={productActionsWrapperClickHandler}
                                        >
                                            <ProductQuantityOrdered
                                                extendedStyles={styles.extendedQtyFieldStyles}
                                                labelOverride=""
                                                id={`${product.id}-qty`}
                                            />
                                            <ProductAddToCartButton
                                                extendedStyles={styles.extendedAddToCartStyles}
                                                iconOnly={true}
                                                data-test-selector={`actionsAddToCart${product.id}`}
                                                sizeVariant="small"
                                            />
                                        </StyledWrapper>
                                    </SearchAutoCompleteProductAction>
                                )}
                            </StyledWrapper>

                            {product.properties.isVariantParent === "true" && (
                                <Button className="choose-from-options" sizeVariant="small">
                                    {translate("Choose from [count] Options").replace(
                                        "[count]",
                                        product.properties.styledChildrenCount,
                                    )}
                                </Button>
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                ))}
            </>
        );
    }
}

export default AutocompleteProducts;
